import { compressToBase64 } from 'lz-string';
import { PaperScope, Raster, Size } from 'paper/dist/paper-core';
import React, { FC, useEffect, useState } from 'react';
import { graphql, useFragment, useLazyLoadQuery, useMutation } from 'react-relay';
import { UpgradeDialog } from 'src/components/widgets/upgradeDialog/upgradeDialog';
import config from 'src/shared/config';
import { T, stringFormat, toGlobalID, toLocalID } from 'src/shared/helpers';
import { usePageTitle } from 'src/shared/hooks/usePageTitle';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from 'src/components/widgets/notificationToaster';
import { Link } from '../../widgets/buttons';
import { fetchProductQuery } from '../productConfiguration/state/queries/__generated__/fetchProductQuery.graphql';
import { productFragment_model$key } from '../productConfiguration/state/queries/__generated__/productFragment_model.graphql';
import { fetchProduct } from '../productConfiguration/state/queries/fetchProduct';
import { productFragment } from '../productConfiguration/state/queries/productFragment';
import { PlacementOutputAreaItemInput, placementCreatePlacementOutputMutation } from './__generated__/placementCreatePlacementOutputMutation.graphql';
import { placementQuery, placementQuery$data } from './__generated__/placementQuery.graphql';
import { placementUpdatePlacementOutputMutation } from './__generated__/placementUpdatePlacementOutputMutation.graphql';
import PlacementAreaDesigner, { PlacementEditorHandle } from './placementAreaDesigner';
import { ItemType, ppcm } from './shared/sharedPlacementComponents';
import { PlacementInput } from './utils';

const UpgradeBanner = styled(UpgradeDialog)`
    margin-bottom: 20px;
    p {
        margin: 0;
        padding: 0;
        line-height: 18px;
    }
`;

const UpgradeBannerContent = styled.p``;

export const createPlacementMutation = graphql`
    mutation placementCreatePlacementOutputMutation($input: PlacementOutputInputCreateInput, $inputCompressed: String) {
        createPlacementOutput(input: $input, inputCompressed: $inputCompressed) {
            placementOutput {
                id
                modelID
                widthCM
                heightCM
                dpi
                isEnabled
                templateFileObjectID
                templateFileID
                jsonArea
                name
                placementOutputAreaItems {
                    placementOutputAreaItemID
                    colorID
                    sideID
                    areaID
                    json
                    svg
                }
                placementOutputItems {
                    placementOutputItemID
                    json
                    colorID
                }
                placementOutputLayers {
                    colorID
                    fileID
                    jsonArea
                    fileObjectID
                    placementOutputLayerID
                }
                settings {
                    placementOutputFileType
                    backgroundAsFillBackgroundTool
                }
            }
        }
    }
`;

export const updatePlacementMutation = graphql`
    mutation placementUpdatePlacementOutputMutation($input: PlacementOutputInputUpdateInput, $inputCompressed: String) {
        updatePlacementOutput(input: $input, inputCompressed: $inputCompressed) {
            placementOutput {
                id
                modelID
                widthCM
                heightCM
                dpi
                isEnabled
                templateFileObjectID
                templateFileID
                jsonArea
                name
                placementOutputAreaItems {
                    placementOutputAreaItemID
                    colorID
                    sideID
                    areaID
                    json
                    svg
                }
                placementOutputItems {
                    placementOutputItemID
                    json
                    colorID
                }
                placementOutputLayers {
                    colorID
                    fileID
                    jsonArea
                    fileObjectID
                    placementOutputLayerID
                }
                settings {
                    placementOutputFileType
                    backgroundAsFillBackgroundTool
                }
            }
        }
    }
`;

interface PlacementOutputItem {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    color?: string;
    fontSize?: number;
    justification?: 'left' | 'center' | 'right';
    content?: string;
    itemType: number;
    imgUrl?: string;
    guid: string;
    paperJson?: string;
    isPlaceholder?: boolean;
}

const PlacementWithId: FC<{
    modelId: number;
    placementId: number;
    saveRef?: React.MutableRefObject<(() => void) | null>;
    onSavingChange?: (saving: boolean) => void;
    editorRef?: React.MutableRefObject<PlacementEditorHandle | null>;
}> = ({ modelId, placementId, saveRef, onSavingChange, editorRef }) => {
    let placement = useLazyLoadQuery<placementQuery>(
        graphql`
            query placementQuery($id: ID!) {
                placementOutput(id: $id) {
                    id
                    modelID
                    widthCM
                    heightCM
                    dpi
                    isEnabled
                    templateFileObjectID
                    templateFileID
                    jsonArea
                    name
                    placementOutputID
                    placementOutputAreaItems {
                        placementOutputAreaItemID
                        colorID
                        sideID
                        areaID
                        json
                        svg
                    }
                    placementOutputItems {
                        placementOutputItemID
                        json
                    }
                    placementOutputLayers {
                        placementOutputLayerID
                        colorID
                        fileID
                        jsonArea
                        fileObjectID
                    }
                    settings {
                        placementOutputFileType
                        backgroundAsFillBackgroundTool
                    }
                }
            }
        `,
        {
            id: `guid://zakeke/PlacementOutput/${placementId}`
        }
    );

    return <Placement modelId={modelId} placement={placement} saveRef={saveRef} onSavingChange={onSavingChange} editorRef={editorRef} />;
};

const PlacementPage: FC<{
    modelId: number;
    placementId?: number | undefined | null;
    saveRef?: React.MutableRefObject<(() => void) | null>;
    onSavingChange?: (saving: boolean) => void;
    editorRef?: React.MutableRefObject<PlacementEditorHandle | null>;
}> = ({ modelId, placementId, saveRef, onSavingChange, editorRef }) => {
    if (placementId) {
        return <PlacementWithId modelId={modelId} placementId={placementId} saveRef={saveRef} onSavingChange={onSavingChange} editorRef={editorRef} />;
    }

    return <Placement modelId={modelId} placement={null} saveRef={saveRef} onSavingChange={onSavingChange} editorRef={editorRef} />;
};

const PlacementContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const Placement: FC<{
    modelId: number;
    placement: placementQuery$data | null;
    saveRef?: React.MutableRefObject<(() => void) | null>;
    onSavingChange?: (saving: boolean) => void;
    editorRef?: React.MutableRefObject<PlacementEditorHandle | null>;
}> = ({ modelId, placement, saveRef, onSavingChange, editorRef }) => {
    usePageTitle(T._('Advanced Print File Builder', 'Admin'));
    if (!modelId) {
        throw new Error(`Invalid placement id ${modelId}`);
    }

    const [createPlacement, createPlacementInProgress] = useMutation<placementCreatePlacementOutputMutation>(createPlacementMutation);
    const [updatePlacement, updatePlacementInProgress] = useMutation<placementUpdatePlacementOutputMutation>(updatePlacementMutation);

    const savingInProgress = createPlacementInProgress || updatePlacementInProgress;

    useEffect(() => {
        onSavingChange?.(savingInProgress);
    }, [savingInProgress, onSavingChange]);

    const [placementId, setPlacementId] = useState<number | undefined | null>(
        placement?.placementOutput != null ? toLocalID(placement?.placementOutput.id) : null
    );

    const queryModel = useLazyLoadQuery<fetchProductQuery>(fetchProduct, {
        id: `guid://zakeke/Model/${modelId}`
    });

    const model = useFragment<productFragment_model$key>(productFragment, queryModel.node?.productFragment_model ?? null);
    const { subscription, ecommerceName, sellerMarketplace, updateSubscription } = useSubscriptionData();
    const [upgradeBannerVisible, setUpgradeBannerVisible] = useState(true);

    const handleSavePlacement = async (placement: PlacementInput) => {
        const colorsInAreas = [...new Set(placement.areas.map((area) => area.colorID))];

        const placementOutputItems = await Promise.all(
            placement.items.map(async (item) => {
                const paperScope = new PaperScope();
                paperScope.setup(new Size(1, 1));

                const paperItem = paperScope.project.activeLayer.importJSON(item.path);

                const itemLoaded = await new Promise<any>((resolve) => {
                    if (paperItem instanceof Raster) {
                        paperItem.onLoad = () => {
                            resolve(paperItem);
                        };
                    } else {
                        resolve(paperItem);
                    }
                });
                itemLoaded.rotate(-(item.rotation ?? 0));

                const placeholderJson = {
                    x: itemLoaded.bounds.x + (placement.width * ppcm) / 2,
                    y: itemLoaded.bounds.y + (placement.heigth * ppcm) / 2,
                    width: itemLoaded.bounds.width,
                    height: itemLoaded.bounds.height,
                    name: item.name,
                    rotation: item.rotation,
                    scaling: item.scaling,
                    color: item.color,
                    fontSize: item.fontSize,
                    content: item.content,
                    itemType: Object.keys(ItemType).indexOf(item.itemType),
                    imgUrl: item.imgUrl,
                    guid: item.guid,
                    paperJson: item.path,
                    isPlaceholder: item.isPlaceholder,
                    justification: item.textAlignment
                } as PlacementOutputItem;

                return {
                    placementOutputItemID: item.placementOutputItemID ?? -1,
                    json: JSON.stringify(placeholderJson)
                };
            })
        );

        const placementOutputAreaItems = placement.areas.map((area) => {
            const paperScope = new PaperScope();
            paperScope.setup(new Size(1, 1));

            const areaItem = paperScope.project.activeLayer.importJSON(area.path);
            areaItem.rotate(-(area.rotation ?? 0));
            areaItem.data.visible = area.visible ?? true;
            areaItem.data.labelVisibility = area.labelVisibility ?? true;

            const rect = `<rect x="${areaItem.bounds.x + (placement.width * 100) / 2}" y="${areaItem.bounds.y + (placement.heigth * 100) / 2}" width="${
                areaItem.bounds.width
            }" height="${areaItem.bounds.height}" data-rotation="${area.rotation}" transform-origin='rotate(${area.rotation})' rx="0" ry="0"></rect>`;
            const svgPlacement = `<!--<svg xmlns="http://www.w3.org/2000/svg">-->${rect}<!--</svg>-->`;

            const placementOutputAreaItem = {
                placementOutputAreaItemID: area.placementOutputAreaItemID ?? -1,
                colorID: area.colorID,
                sideID: area.sideID,
                areaID: area.areaID,
                json: areaItem.exportJSON(),
                svg: svgPlacement
            } as PlacementOutputAreaItemInput;

            return placementOutputAreaItem;
        });

        const layers = placement.layers
            .filter((layer) => colorsInAreas.includes(layer.colorID))
            .map((layer) => {
                const l = {
                    placementOutputLayerID: layer.id ?? -1,
                    colorID: layer.colorID,
                    fileID: layer.fileID,
                    jsonArea: layer.jsonArea,
                    fileObjectID: layer.fileObjectID ?? -1
                };

                return l;
            });

        const settings = placement.settings;

        const generalPdfLayer = placement.layers.find((l) => l.colorID === -1);

        const input = {
            modelID: modelId,
            dpi: placement.dpi,
            widthCM: placement.width,
            heightCM: placement.heigth,
            isEnabled: 1,
            name: placement.name,
            templateFileID: generalPdfLayer?.fileID ?? null,
            jsonArea: generalPdfLayer?.jsonArea ?? null,
            templateFileObjectID: generalPdfLayer?.fileObjectID ?? null,
            placementOutputAreaItems: placementOutputAreaItems,
            placementOutputItems: placementOutputItems,
            placementOutputLayers: layers,
            settings: settings
        };

        const savingNotificationId = showInfoNotification({ message: T._('Saving...', 'Admin') });

        if (placementId) {
            const inputforUpdatedPlacement = { id: toGlobalID(placementId, 'PlacementOutput'), ...input };

            updatePlacement({
                variables: {
                    input: config.enableCompressionForSavingReadingPlacements ? null : inputforUpdatedPlacement,
                    inputCompressed: config.enableCompressionForSavingReadingPlacements ? compressToBase64(JSON.stringify(inputforUpdatedPlacement)) : null
                },
                onCompleted: () => {
                    if (savingNotificationId) removeNotification(savingNotificationId);
                    showSuccessNotification({ message: T._('Saved', 'Admin') });
                },
                onError: () => {
                    if (savingNotificationId) removeNotification(savingNotificationId);
                    showErrorNotification({ message: T._('Error saving placement', 'Admin') });
                },
                updater: (store) => store.invalidateStore()
            });
        } else {
            createPlacement({
                variables: {
                    input: config.enableCompressionForSavingReadingPlacements ? null : input,
                    inputCompressed: config.enableCompressionForSavingReadingPlacements ? compressToBase64(JSON.stringify(input)) : null
                },
                onCompleted: (value) => {
                    if (savingNotificationId) removeNotification(savingNotificationId);
                    showSuccessNotification({ message: T._('Saved', 'Admin') });

                    let newPlacementId = value?.createPlacementOutput?.placementOutput?.id;

                    if (newPlacementId) {
                        setPlacementId(toLocalID(newPlacementId));
                    }
                },
                onError: () => {
                    if (savingNotificationId) removeNotification(savingNotificationId);
                    showErrorNotification({ message: T._('Error saving placement', 'Admin') });
                },
                updater: (store) => {
                    // add the new placement in the relay store
                    const root = store.getRoot();
                    const newPlacement = store.getRootField('createPlacementOutput').getLinkedRecord('placementOutput');
                    const oldPlacements = store.getRoot().getLinkedRecords('placementsOutput', { id: `guid://Zakeke/Placements/${modelId}` }) as [];
                    const allPlacements = [...oldPlacements, newPlacement];
                    root.setLinkedRecords(allPlacements, 'placementsOutput', { id: `guid://Zakeke/Placements/${modelId}` });
                }
            });
        }
    };

    return (
        <PlacementContainer>
            {!subscription?.plan?.additionalFeatures.advancedPrintFilesBuilder && upgradeBannerVisible && (
                <UpgradeBanner
                    redirectUrlFromDashboard={encodeURI(window.location.href)}
                    type={'BANNER'}
                    marketPlace={sellerMarketplace ?? undefined}
                    showCloseButton={true}
                    showUpgradeButtonAsLink={true}
                    onClose={() => setUpgradeBannerVisible(false)}
                    message={
                        sellerMarketplace === 'MICROSOFT_AZURE' ? (
                            <UpgradeBannerContent>
                                {stringFormat(T._('Upgrade to the {0} plan for custom', 'Admin'), 'Grow')}{' '}
                                <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/9686184040348-Advanced-Print-Files-Builder'>
                                    {T._('advanced print-ready files', 'Admin')}
                                </Link>{' '}
                                {T._('Go to your Zakeke app Saas from Azure portal to upgrade the plan', 'Admin')}
                            </UpgradeBannerContent>
                        ) : ecommerceName === 'SHOPIFY' ? (
                            <UpgradeBannerContent>
                                {stringFormat(T._('Upgrade to the {0} plan for custom', 'Admin'), 'Grow')}{' '}
                                <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/9686184040348-Advanced-Print-Files-Builder'>
                                    {T._('advanced print-ready files', 'Admin')}
                                </Link>{' '}
                                {T._("Once you click to upgrade, you'll be redirected to the Shopify page for plan approval", 'Admin')}
                            </UpgradeBannerContent>
                        ) : (
                            <UpgradeBannerContent>
                                {T._('Your current plan does not include the ability to create custom', 'Admin')}{' '}
                                <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/9686184040348-Advanced-Print-Files-Builder'>
                                    {T._('advanced print-ready files', 'Admin')}
                                </Link>
                                {'. '}
                                {stringFormat(T._('Upgrade to the {0} plan to be able to access it.', 'Admin'), 'Grow')}
                            </UpgradeBannerContent>
                        )
                    }
                    onUpgradeCompleted={async () => {
                        updateSubscription();
                    }}
                />
            )}

            <PlacementAreaDesigner
                model={model!}
                savingInProgress={savingInProgress}
                onSaveClick={handleSavePlacement}
                placement={placement}
                saveRef={saveRef}
                editorRef={editorRef}
            />
        </PlacementContainer>
    );
};

export default PlacementPage;
